/**
 * Hooks para CFG-01 (Perfil de Empresa) y CFG-02 (Localización)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useAuthStore } from '@/store/authStore'
import { resolveTax, type TenantTax } from '@/lib/locale'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CompanyProfile {
  id: string | null
  legal_name: string
  trade_name: string
  tax_id: string
  email: string
  phone: string
  website: string
  address_street: string
  /** Donde se reciben las mercaderias. Viaja al SII en el `<Recinto>` del
   *  acuse de recibo; el modal lo precarga y deja cambiarlo por documento. */
  reception_venue?: string
  /** La comuna que va al SII (`CmnaOrigen`). Vacía, el XML informa la ciudad (#1618). */
  address_commune?: string
  address_city: string
  address_region: string
  address_postal_code: string
  address_country: string
  business_activity: string
  business_activity_code: string
  sidebar_color: string
  primary_color: string
  accent_color: string
  disabled_modules: string[]
  show_clobi_footer: boolean
  /** Publica el organigrama en el portal del colaborador (nombre, cargo y jefatura). */
  org_chart_visible_to_employees?: boolean
  /** Cuenta de detracciones del Banco de la Nación (SPOT). Solo Perú. */
  detraction_account?: string
  additional_addresses: string
  logo_url: string | null
  created_at: string | null
  updated_at: string | null
}

export interface LocalizationConfig {
  id: string | null
  country_code: string
  country_display: string
  currency: string
  currency_display: string
  tax_name: string
  tax_rate: string
  is_configured: boolean
  sii_environment: 'homologacion' | 'produccion'
  acteco: string
  email_intercambio_empresas: string
  email_intercambio_sii: string
  email_intercambio_empresas_cert: string
  email_intercambio_sii_cert: string
  email_intl_invoices: string
  email_honorarios: string
  resolucion_number: string
  resolucion_date: string | null
  rut_envia: string
  sii_office: string
  mutual: string
  mutual_rate: string
  ccaf_code?: string
  ccaf_display?: string
  additional_taxes: AdditionalTax[]
  audit_log_retention_days: number
  auto_distribute_dte: boolean
  auto_import_rcv_matched_dte: boolean
  allow_approve_without_xml: boolean
  auto_send_cesion: boolean
  ui_purchases_360: boolean
  regimen_tributario: '' | 'general' | 'propyme_general' | 'propyme_transparente'
  ppm_modo: 'manual' | 'automatico'
  ppm_tasa: string | null
  driver_fields: DriverField[]
  created_at: string | null
  updated_at: string | null
}

export interface AdditionalTax {
  key: string
  label: string
  help_text?: string
  rate: number
}

export interface DriverField {
  key: string
  label: string
  type: 'text' | 'select' | 'boolean' | 'number' | 'file' | 'password'
  options?: { value: string; label: string }[]
  default?: unknown
  required?: boolean
  help_text?: string
  min?: number
  max?: number
  /** For additional tax boolean fields: the configured rate to store when toggled on */
  rate?: number
}

export interface DriverInfo {
  driver: string | null
  country_code: string
  tax_id_label: string
  tax_fields: DriverField[]
  document_types: { code: string; name: string }[]
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export interface FolioConfig {
  id: number
  document_type_code: number
  document_type_label: string
  next_folio: number
  is_active: boolean
  /** Folios restantes a partir de los cuales avisar. null = automatico segun el tamano del rango. */
  alert_threshold: number | null
  alert_enabled: boolean
}

/**
 * Serie de emision para los paises donde el numero lo lleva el emisor (Peru).
 * El analogo de FolioConfig, sin CAF que autorice un rango.
 */
export interface DocumentSeries {
  id: string
  country_code: string
  document_type: 'FE' | 'BE' | 'NC' | 'ND'
  document_type_label: string
  series: string
  next_correlative: number
  /** Como se va a imprimir el proximo numero: "F001-00000452". */
  next_number_preview: string
  /** Cuantos documentos tomaron numero de esta serie. Lo lleva el backend. */
  issued_count: number
  /**
   * Si la serie se puede eliminar. Lo resuelve el backend (`has_issued`): NO se
   * deduce del correlativo, porque una serie puede arrancar en un numero alto
   * para continuar la numeracion de otro sistema sin haber emitido nada.
   */
  can_delete: boolean
  is_default: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export type CustomFieldScope = 'ventas' | 'compras' | 'contratos'
export type CustomFieldLinkEntity = '' | 'customer' | 'sale_document' | 'supplier_invoice'

export interface GlobalCustomField {
  id: number
  nombre: string
  tipo: 'string' | 'numero' | 'fecha' | 'select' | 'multiselect' | 'boolean' | 'entity_link'
  tipo_label: string
  scope: CustomFieldScope
  scope_label?: string
  link_entity: CustomFieldLinkEntity
  link_entity_label?: string
  is_required: boolean
  usable_in_filters: boolean
  default_value: string
  opciones: string[]
  order: number
  is_active: boolean
}

export interface InvoicerCredentialField {
  key: string
  label: string
  type: 'text' | 'password'
  required: boolean
}

export interface InvoicerUrlField {
  key: string
  label: string
}

export interface InvoicerConfig {
  id: string | null
  provider_type: string
  provider_type_display: string
  is_active: boolean
  has_credentials: boolean
  credential_fields: InvoicerCredentialField[]
  url_fields: InvoicerUrlField[]
  urls_config: Record<string, Record<string, string>>
  effective_urls: Record<string, Record<string, string>>
  notes: string
  created_at: string | null
  updated_at: string | null
}

export interface InvoicerProvider {
  code: string
  label: string
  credential_fields: InvoicerCredentialField[]
  url_fields: InvoicerUrlField[]
  default_urls: Record<string, Record<string, string>>
}

export interface InvoicerTestResult {
  success: boolean
  message: string
  details: Record<string, { ok: boolean; error?: string; status_code?: number }>
}

export interface PdfTemplate {
  id: string
  name: string
  document_type: string
  document_type_display: string
  additional_document_types: string[]
  applies_to_all: boolean
  page_size: string
  page_size_display: string
  is_default: boolean
  layout_config: Record<string, unknown>
  custom_css: string
  created_at: string
  updated_at: string
}

export const configKeys = {
  profile: ['config', 'profile'] as const,
  localization: ['config', 'localization'] as const,
  driverInfo: ['config', 'driver-info'] as const,
  folios: ['config', 'folios'] as const,
  documentSeries: ['config', 'document-series'] as const,
  customFields: ['config', 'custom-fields'] as const,
  invoicer: ['config', 'invoicer'] as const,
  invoicerProviders: ['config', 'invoicer-providers'] as const,
  apiKeys: ['config', 'api-keys'] as const,
  apiKeyUsage: ['config', 'api-key-usage'] as const,
  apiKeyAlerts: ['config', 'api-key-alerts'] as const,
  apiKeyGlobalUsage: ['config', 'api-key-global-usage'] as const,
  currencyRates: ['config', 'currency-rates'] as const,
  certificate: ['config', 'certificate'] as const,
  cafs: ['config', 'cafs'] as const,
  pdfTemplates: ['config', 'pdf-templates'] as const,
}

// ─── CFG-01: Perfil ───────────────────────────────────────────────────────────

export function useCompanyProfile() {
  return useQuery<CompanyProfile>({
    queryKey: configKeys.profile,
    queryFn: async () => {
      const res = await apiClient.get('/config/profile/')
      return res.data
    },
  })
}

export function useUpdateCompanyProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: FormData | Partial<CompanyProfile>) => {
      const res = await apiClient.patch('/config/profile/', data, {
        headers: data instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : { 'Content-Type': 'application/json' },
      })
      return res.data
    },
    onSuccess: (savedProfile: CompanyProfile) => {
      qc.invalidateQueries({ queryKey: configKeys.profile })
      // Aplicar branding actualizado al instante (sin necesidad de refresh).
      import('@/lib/applyTenantBranding').then(({ applyTenantBranding }) => {
        applyTenantBranding(savedProfile)
      })
      // Actualizar disabled_modules en el store en tiempo real para que
      // el sidebar refleje los cambios sin necesidad de re-login
      if (savedProfile?.disabled_modules !== undefined) {
        const store = useAuthStore.getState()
        const schema = store.activeTenantSchema
        if (schema) {
          const updatedTenants = store.tenants.map(t =>
            t.schema === schema ? { ...t, disabled_modules: savedProfile.disabled_modules } : t
          )
          store.hydrateFromSession({ user: store.user!, tenants: updatedTenants })
          store.setActiveTenant(schema)
        }
      }
    },
  })
}

// ─── CFG-02: Localización ─────────────────────────────────────────────────────

export function useLocalizationConfig() {
  return useQuery<LocalizationConfig>({
    queryKey: configKeys.localization,
    queryFn: async () => {
      const res = await apiClient.get('/config/localization/')
      return res.data
    },
  })
}

/**
 * El impuesto con el que opera este tenant: cómo se llama y a qué tasa.
 *
 * Sale de Configuración → Localización, que es donde se define por tenant y por
 * país (IVA 19% en Chile, IGV 18% en Perú). Mientras esa configuración carga
 * cae en el respaldo del país, no en un valor fijo.
 *
 * ⚠️ Usar esto en vez de escribir `19` o «IVA» en una pantalla. La tasa
 * horneada era un error silencioso: el backend recalcula el impuesto con
 * `LocalizationConfig.tax_rate` (`purchases/serializers.py`,
 * `sales/serializers.py`), así que una pantalla que la asume distinta muestra
 * un total y deja guardado otro.
 */
export function useTenantTax(): TenantTax {
  const { data: loc } = useLocalizationConfig()
  const country = useAuthStore(s => s.activeTenant?.country)
  return resolveTax(loc?.country_code || country, loc?.tax_name, loc?.tax_rate)
}

export function useUpdateLocalizationConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<LocalizationConfig>) => {
      const res = await apiClient.patch('/config/localization/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.localization })
      qc.invalidateQueries({ queryKey: configKeys.driverInfo })
    },
  })
}

export function useDriverInfo() {
  return useQuery<DriverInfo>({
    queryKey: configKeys.driverInfo,
    queryFn: async () => {
      const res = await apiClient.get('/config/localization/driver-info/')
      return res.data
    },
  })
}

// ─── CFG-03: Pila de Folios ───────────────────────────────────────────────────

export function useFolioConfigs() {
  return useQuery<FolioConfig[]>({
    queryKey: configKeys.folios,
    queryFn: async () => {
      const res = await apiClient.get('/config/folios/')
      return res.data
    },
  })
}

export function useUpdateFolioConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      code,
      data,
    }: {
      code: number
      data: Partial<Pick<FolioConfig, 'next_folio' | 'is_active' | 'alert_threshold' | 'alert_enabled'>>
    }) => {
      const res = await apiClient.patch(`/config/folios/${code}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.folios })
    },
  })
}

// ─── Series de emision (paises sin CAF: Peru) ─────────────────────────────────

export function useDocumentSeries() {
  return useQuery<DocumentSeries[]>({
    queryKey: configKeys.documentSeries,
    queryFn: async () => {
      const res = await apiClient.get('/config/document-series/')
      return res.data
    },
  })
}

export function useCreateDocumentSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      data: Pick<DocumentSeries, 'document_type' | 'series'> &
        Partial<Pick<DocumentSeries, 'next_correlative' | 'is_default'>>
    ) => {
      const res = await apiClient.post('/config/document-series/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.documentSeries })
    },
  })
}

export function useUpdateDocumentSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<Pick<DocumentSeries, 'next_correlative' | 'is_default' | 'is_active'>>
    }) => {
      const res = await apiClient.patch(`/config/document-series/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.documentSeries })
    },
  })
}

export function useDeleteDocumentSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/config/document-series/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.documentSeries })
    },
  })
}

// ─── CFG-04: Campos Personalizados Globales ───────────────────────────────────

export function useGlobalCustomFields(activeOnly?: boolean, scope?: CustomFieldScope | 'all') {
  return useQuery<GlobalCustomField[]>({
    queryKey: [...configKeys.customFields, activeOnly ? 'active' : 'all-states', scope || 'default'],
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (activeOnly) qs.set('active', 'true')
      if (scope) qs.set('scope', scope)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await apiClient.get(`/config/custom-fields/${suffix}`)
      return res.data
    },
  })
}

export function useCreateGlobalCustomField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Omit<GlobalCustomField, 'id' | 'tipo_label'>) => {
      const res = await apiClient.post('/config/custom-fields/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.customFields })
    },
  })
}

export function useUpdateGlobalCustomField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: Partial<Omit<GlobalCustomField, 'id' | 'tipo_label'>>
    }) => {
      const res = await apiClient.patch(`/config/custom-fields/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.customFields })
    },
  })
}

export function useDeleteGlobalCustomField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/config/custom-fields/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.customFields })
    },
  })
}

// ─── CFG-05: Facturador Electrónico ───────────────────────────────────────────

export function useInvoicerConfig() {
  return useQuery<InvoicerConfig>({
    queryKey: configKeys.invoicer,
    queryFn: async () => {
      const res = await apiClient.get('/config/invoicer/')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useUpdateInvoicerConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      provider_type?: string
      is_active?: boolean
      credentials?: Record<string, string>
      urls_config?: Record<string, Record<string, string>>
      notes?: string
    }) => {
      const res = await apiClient.post('/config/invoicer/', data)
      return res.data as InvoicerConfig
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.invoicer })
    },
  })
}

export function useTestInvoicer() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/config/invoicer/test/')
      return res.data as InvoicerTestResult
    },
  })
}

export function useInvoicerProviders() {
  return useQuery<InvoicerProvider[]>({
    queryKey: configKeys.invoicerProviders,
    queryFn: async () => {
      const res = await apiClient.get('/config/invoicer/providers/')
      return res.data
    },
    staleTime: 300_000,
  })
}

// ─── SSO: Configuración del tenant ───────────────────────────────────────────

export interface TenantSSOConfig {
  id: string
  auth_mode: 'password_only' | 'sso_only' | 'both'
  auth_mode_display: string
  google_enabled: boolean
  // SAML IdP
  saml_enabled: boolean
  saml_allow_idp_initiated: boolean
  saml_entity_id: string
  saml_sso_url: string
  saml_metadata_url: string
  saml_name_id_format: string
  saml_attribute_mapping: Record<string, string>
  // IdP cert (never returned; only metadata)
  has_saml_idp_certificate: boolean
  saml_idp_cert_not_after: string | null
  // SP cert (public)
  saml_sp_certificate: string
  saml_sp_cert_not_after: string | null
  created_at: string
  updated_at: string
}

// Payload for PATCH /config/sso/ — includes write-only saml_certificate field
export interface UpdateSSOConfigPayload extends Partial<Omit<TenantSSOConfig, 'has_saml_idp_certificate' | 'saml_sp_certificate'>> {
  saml_certificate?: string   // write-only: PEM text of IdP cert
}

export function useSSOConfig() {
  return useQuery<TenantSSOConfig>({
    queryKey: [...configKeys.profile, 'sso'] as const,
    queryFn: async () => {
      const res = await apiClient.get('/config/sso/')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useUpdateSSOConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateSSOConfigPayload) => {
      const res = await apiClient.patch('/config/sso/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...configKeys.profile, 'sso'] })
    },
  })
}

export function useRegenerateSAMLSPCert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/config/sso/saml/regenerate-sp-cert/')
      return res.data as { sp_certificate: string; not_after: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...configKeys.profile, 'sso'] })
    },
  })
}

export function useSAMLMetadataUrl(): string {
  const base = window.location.origin
  return `${base}/api/v1/auth/saml/metadata/`
}

// ─── API Keys ─────────────────────────────────────────────────────────────────

export interface APIKeyItem {
  id: string
  name: string
  prefix: string
  scopes: string[]
  is_active: boolean
  last_used_at: string | null
  expires_at: string | null
  created_at: string
  key?: string  // solo se retorna al crear
}

export function useAPIKeys() {
  return useQuery<{ count: number; api_keys: APIKeyItem[] }>({
    queryKey: configKeys.apiKeys,
    queryFn: async () => {
      const res = await apiClient.get('/config/api-keys/')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useCreateAPIKey() {
  const qc = useQueryClient()
  return useMutation<APIKeyItem, Error, { name: string; scopes: string[]; expires_in_days?: number }>({
    mutationFn: async (data) => {
      const res = await apiClient.post('/config/api-keys/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.apiKeys })
    },
  })
}

export function useUpdateAPIKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<APIKeyItem> }) => {
      const res = await apiClient.patch(`/config/api-keys/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.apiKeys })
    },
  })
}

export function useDeleteAPIKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/config/api-keys/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.apiKeys })
    },
  })
}

// ─── API Key Usage & Alerts ──────────────────────────────────────────────────

export interface APIKeyUsageData {
  key_id: string
  key_name: string
  period_days: number
  summary: {
    total_requests: number
    successful: number
    errors: number
    error_rate: number
    avg_response_ms: number
    unique_ips: number
  }
  daily: { date: string; requests: number; errors: number }[]
  top_endpoints: { method: string; endpoint: string; count: number }[]
  recent_errors: {
    method: string
    endpoint: string
    status_code: number
    error_detail: string
    timestamp: string
    ip_address: string
  }[]
}

export interface APIKeyAlert {
  type: string
  severity: 'critical' | 'warning' | 'info'
  key_id: string
  key_name: string
  message: string
  timestamp: string
}

export interface APIKeyGlobalUsage {
  period_days: number
  total_requests: number
  total_errors: number
  error_rate: number
  per_key: {
    key_id: string
    key_name: string
    prefix: string
    requests: number
    errors: number
    error_rate: number
    avg_response_ms: number
  }[]
}

export interface ScopeGroup {
  label: string
  children: Record<string, string>
  /** Qué habilita cada scope, redactado para quien integra. Lo sirve el backend
   *  desde `apps.public_api.scopes`, que es la fuente única de la superficie
   *  pública — la lista ya no es el catálogo de módulos internos. */
  descriptions?: Record<string, string>
}

export function useAPIKeyScopes() {
  return useQuery<{ scopes: string[]; groups: Record<string, ScopeGroup> }>({
    queryKey: [...configKeys.apiKeys, 'scopes'],
    queryFn: async () => {
      const res = await apiClient.get('/config/api-keys/scopes/')
      return res.data
    },
    staleTime: 300_000,
  })
}

export function useAPIKeyUsage(keyId: string, days = 7) {
  return useQuery<APIKeyUsageData>({
    queryKey: [...configKeys.apiKeyUsage, keyId, days],
    queryFn: async () => {
      const res = await apiClient.get(`/config/api-keys/${keyId}/usage/?days=${days}`)
      return res.data
    },
    enabled: !!keyId,
    staleTime: 60_000,
  })
}

export function useAPIKeyAlerts() {
  return useQuery<{ count: number; alerts: APIKeyAlert[] }>({
    queryKey: configKeys.apiKeyAlerts,
    queryFn: async () => {
      const res = await apiClient.get('/config/api-keys/alerts/')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useAPIKeyGlobalUsage(days = 7) {
  return useQuery<APIKeyGlobalUsage>({
    queryKey: [...configKeys.apiKeyGlobalUsage, days],
    queryFn: async () => {
      const res = await apiClient.get(`/config/api-keys/usage/?days=${days}`)
      return res.data
    },
    staleTime: 60_000,
  })
}

// ─── CFG-06: Tipos de Cambio ─────────────────────────────────────────────────

export interface CurrencyRateInfo {
  rate: string
  rate_date: string
  label: string
  source: string
}

export interface CurrencyRatesResponse {
  rates: Record<string, CurrencyRateInfo>
}

export function useCurrencyRates() {
  return useQuery<CurrencyRatesResponse>({
    queryKey: configKeys.currencyRates,
    queryFn: async () => {
      const { data } = await apiClient.get('/config/currency-rates/')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useSyncCurrencyRates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (days?: number) => {
      const params = days ? `?days=${days}` : ''
      const { data } = await apiClient.post(`/config/currency-rates/sync/${params}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.currencyRates })
    },
  })
}

// ─── Certificados y CAF ──────────────────────────────────────────────────────

export interface CertificateInfo {
  has_certificate: boolean
  subject?: string
  issuer?: string
  valid_from?: string
  valid_until?: string
  days_remaining?: number
  is_valid?: boolean
  serial?: string
  error?: string
}

export interface CAFGapRange {
  from: number
  to: number
}

export interface CAFDetail {
  valid_from: number
  valid_to: number
  total: number
  disponibles: number
  gaps: CAFGapRange[]
  gaps_count: number
}

export interface CAFInfo {
  document_type_code: number
  document_type_label: string
  next_folio: number
  has_caf: boolean
  caf_valid_from: number | null
  caf_valid_to: number | null
  folios_disponibles: number | null
  folios_total: number | null
  /** Entregados a cajas del POS y sin usar: no están en `folios_disponibles`. */
  folios_en_terminales?: number
  alert: boolean
  /** 'ok' | 'por_agotarse' | 'agotado' — resuelto con el umbral del tipo de documento. */
  estado?: 'ok' | 'por_agotarse' | 'agotado' | null
  /** Umbral realmente aplicado (el configurado, o el automatico). */
  alert_threshold_efectivo?: number | null
  alert_threshold?: number | null
  alert_enabled?: boolean
  cafs?: CAFDetail[]
  gaps_count?: number
}

export function useCertificateInfo() {
  return useQuery<CertificateInfo>({
    queryKey: configKeys.certificate,
    queryFn: async () => {
      const { data } = await apiClient.get('/config/certificate/')
      return data
    },
  })
}

export function useUploadCertificate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post('/config/certificate/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.certificate })
    },
  })
}

export function useCAFList() {
  return useQuery<CAFInfo[]>({
    queryKey: configKeys.cafs,
    queryFn: async () => {
      const { data } = await apiClient.get('/config/caf/')
      return data
    },
  })
}

export function useUploadCAF() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post('/config/caf/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.cafs })
      qc.invalidateQueries({ queryKey: configKeys.folios })
    },
  })
}

// ─── Solicitud de folios al SII ─────────────────────────────────────────────
//
// 🔴 Son tres hooks y no uno, y la separación importa: consultar es lectura pura
// y solicitar es IRREVERSIBLE — un folio autorizado queda registrado a nombre del
// contribuyente y no se puede devolver.

export interface TramoSinUsar {
  desde: number
  hasta: number
  cantidad: number
  caf_desde: number
  caf_hasta: number
}

export interface UltimaSolicitud {
  id: string
  status: string
  status_label: string
  cantidad: number
  folio_desde: number | null
  folio_hasta: number | null
  /** 🔴 Si es `true`, los folios existen en el SII: NO hay que volver a pedir. */
  folios_ya_existen: boolean
  notes: string
  requested_at: string
}

export interface EstadoFolios {
  puede_solicitar: boolean
  reason_blocked: string
  code: string
  folios_sin_usar: number
  tramos_sin_usar: TramoSinUsar[]
  ultima_solicitud: UltimaSolicitud | null
}

export interface ConsultaSii {
  document_type_code: number
  /** `disponible` · `sin_historia` · `restringido` */
  estado: string
  max_autorizado: number | null
  folios_disponibles: number | null
  puede_pedir: boolean
  explicacion: string
  /** El mensaje del SII tal cual, cuando no autoriza. */
  mensaje_del_sii: string
  requiere_atencion: boolean
}

/** Lo que Clobi sabe. No toca el SII, así que es barato y se puede pedir siempre. */
export function useEstadoFolios(documentTypeCode: number, enabled = true) {
  return useQuery<EstadoFolios>({
    queryKey: [...configKeys.folios, 'estado', documentTypeCode],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/config/folios/${documentTypeCode}/estado/`
      )
      return data
    },
    enabled,
  })
}

/**
 * 🔎 Le pregunta al SII cuánto autoriza. **Lectura pura, pero lenta**: abre un
 * navegador contra el portal, así que la UI tiene que mostrar que está trabajando.
 */
export function useConsultarSii() {
  return useMutation<ConsultaSii, unknown, number>({
    mutationFn: async (documentTypeCode: number) => {
      const { data } = await apiClient.post(
        `/config/folios/${documentTypeCode}/consultar/`
      )
      return data
    },
  })
}

/** 🔴 Pide los folios. **Irreversible.** Responde 202: el trámite queda encolado. */
export function useSolicitarFolios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { documentTypeCode: number; cantidad: number }) => {
      const { data } = await apiClient.post(
        `/config/folios/${vars.documentTypeCode}/solicitar/`,
        { cantidad: vars.cantidad }
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.folios })
      qc.invalidateQueries({ queryKey: configKeys.cafs })
    },
  })
}

// ─── Plantillas PDF ─────────────────────────────────────────────────────────

export function usePdfTemplates(documentType?: string) {
  return useQuery<PdfTemplate[]>({
    queryKey: documentType
      ? [...configKeys.pdfTemplates, documentType]
      : configKeys.pdfTemplates,
    queryFn: async () => {
      const params = documentType ? `?document_type=${documentType}` : ''
      const { data } = await apiClient.get(`/config/pdf-templates/${params}`)
      return data
    },
  })
}

export function usePdfTemplate(id: string | null) {
  return useQuery<PdfTemplate>({
    queryKey: [...configKeys.pdfTemplates, id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/config/pdf-templates/${id}/`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreatePdfTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PdfTemplate>) => {
      const res = await apiClient.post('/config/pdf-templates/', data)
      return res.data as PdfTemplate
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.pdfTemplates })
    },
  })
}

export function useUpdatePdfTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PdfTemplate> }) => {
      const res = await apiClient.patch(`/config/pdf-templates/${id}/`, data)
      return res.data as PdfTemplate
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.pdfTemplates })
    },
  })
}

export function useDeletePdfTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/config/pdf-templates/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.pdfTemplates })
    },
  })
}


// ─── DTE Type Config ─────────────────────────────────────────────────────────

export interface DTETypeConfigItem {
  sii_doc_type: number
  internal_code: string
  display_name: string
  is_active: boolean
  enabled_fields: string[]
  mandatory_field_count: number
  configurable_field_count: number
  enabled_field_count: number
  display_order: number
}

export interface DTEFieldDef {
  key: string
  xml_tag: string
  zone: string
  label: string
  description: string
  data_type: string
  max_length: number
  decimal_places: number
  enum_values: string[]
  enum_labels: string[]
  obligation: number
  depends_on: string[]
  order: number
  group: string
  default_enabled: boolean
  repeatable: boolean
  max_repeat: number
  is_zone_toggle: boolean
}

export interface DTEFieldSchema {
  sii_doc_type: number
  document_type: { code: string; name: string }
  fields_by_zone: Record<string, DTEFieldDef[]>
}

export function useDTETypes() {
  return useQuery<DTETypeConfigItem[]>({
    queryKey: ['config', 'dte-types'],
    queryFn: async () => {
      const res = await apiClient.get('/config/dte-types/')
      return res.data
    },
  })
}

export function useDTEFieldSchema(siiCode?: number) {
  return useQuery<DTEFieldSchema>({
    queryKey: ['config', 'dte-schema', siiCode],
    queryFn: async () => {
      const res = await apiClient.get(`/config/dte-types/schema/?sii_code=${siiCode}`)
      return res.data
    },
    enabled: !!siiCode,
  })
}

export function useUpdateDTEType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { sii_code: number; is_active?: boolean; enabled_fields?: string[] }) => {
      const { sii_code, ...body } = data
      const res = await apiClient.patch(`/config/dte-types/${sii_code}/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', 'dte-types'] })
    },
  })
}


// ─── Account Status ───────────────────────────────────────────────────────────

export interface AccountStatus {
  account_type: 'production' | 'trial' | 'demo'
  account_type_display: string
  is_restricted: boolean
  allow_homologation: boolean
  trial_ends_at: string | null
  days_remaining: number | null
  status: string
}

export function useAccountStatus() {
  return useQuery<AccountStatus>({
    queryKey: ['config', 'account-status'],
    queryFn: async () => {
      const res = await apiClient.get('/config/account-status/')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

// ─── AUD-01: Bitácora de actividad ───────────────────────────────────────────

export interface AuditLogActor {
  id: number
  email: string | null
  name: string | null
}

export interface AuditLogEntry {
  id: number
  timestamp: string
  actor: AuditLogActor | null
  action: string
  action_code: number
  module: string
  module_code: string
  model: string
  model_code: string
  object_id: string | null
  object_repr: string
  changes: Record<string, [unknown, unknown]> | null
  remote_addr: string | null
}

export interface AuditLogResponse {
  count: number
  page: number
  page_size: number
  total_pages: number
  results: AuditLogEntry[]
}

export interface AuditLogFilters {
  actor_id?: string
  module?: string
  model?: string
  action?: string
  date_from?: string
  date_to?: string
  search?: string
  page?: number
  page_size?: number
}

export interface AuditLogModule {
  code: string
  label: string
  models: { code: string; label: string }[]
}

export interface AuditLogMeta {
  modules: AuditLogModule[]
  actions: { code: number; label: string }[]
}

export function useAuditLog(filters: AuditLogFilters) {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
  )
  return useQuery<AuditLogResponse>({
    queryKey: ['config', 'audit-log', params],
    queryFn: async () => {
      const res = await apiClient.get('/config/audit-log/', { params })
      return res.data
    },
    staleTime: 30 * 1000,
  })
}

export function useAuditLogMeta() {
  return useQuery<AuditLogMeta>({
    queryKey: ['config', 'audit-log-modules'],
    queryFn: async () => {
      const res = await apiClient.get('/config/audit-log/modules/')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

// ─── SSO tenant helpers ───────────────────────────────────────────────────────

/**
 * Resuelve el estado SSO del tenant para decidir si se ofrece invitación SSO-only.
 */
export function useTenantSSOEnabled() {
  const { data: ssoConfig, isLoading } = useSSOConfig()
  return {
    isLoading,
    isSSOEnabled: Boolean(ssoConfig?.google_enabled || ssoConfig?.saml_enabled),
    authMode: ssoConfig?.auth_mode ?? 'both',
    canInviteWithoutPassword:
      ssoConfig?.auth_mode !== 'password_only' &&
      Boolean(ssoConfig?.google_enabled || ssoConfig?.saml_enabled),
    forcesSSOOnly: ssoConfig?.auth_mode === 'sso_only',
  }
}
